import React, { useState } from 'react';
import { User, Phone, Mail, X, Loader } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactFormModal = ({ 
  isOpen, 
  onClose, 
  serviceType, 
  title, 
  description,
  showLocation = false,
  isInquiry = false 
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    location: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/interaction`, {
        ...formData,
        serviceType: serviceType
      });

      if (isInquiry) {
        // For inquiries (Personal Training), show success message
        setSuccess(true);
        setTimeout(() => {
          onClose();
          setSuccess(false);
          setFormData({ firstName: '', lastName: '', phone: '', email: '', location: '' });
        }, 3000);
      } else {
        // For paid services, redirect to PayPal
        const paymentLink = response.data.paymentLink;
        if (paymentLink) {
          window.open(paymentLink, '_blank');
        }
        onClose();
        setFormData({ firstName: '', lastName: '', phone: '', email: '', location: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        padding: '20px',
        overflowY: 'auto'
      }} 
      onClick={onClose}
      data-testid="contact-form-modal"
    >
      <div 
        style={{
          background: 'var(--bg-card)',
          borderRadius: '32px',
          padding: 'var(--spacing-xl)',
          maxWidth: '600px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0, 69, 52, 0.3)',
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'relative'
        }} 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px'
          }}
          data-testid="close-modal-button"
        >
          <X size={24} style={{ color: 'var(--text-secondary)' }} />
        </button>

        {success ? (
          <div style={{ textAlign: 'center', padding: 'var(--spacing-xl)' }} data-testid="success-message">
            <div style={{
              width: '80px',
              height: '80px',
              background: 'var(--brand-primary)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto var(--spacing-large)'
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--brand-dark)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h3 className="heading-2" style={{ marginBottom: 'var(--spacing-medium)' }}>
              Vielen Dank!
            </h3>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Ihre Anfrage wurde erfolgreich übermittelt. Wir melden uns in Kürze bei Ihnen.
            </p>
          </div>
        ) : (
          <>
            <h3 className="heading-2" style={{ marginBottom: 'var(--spacing-small)', textAlign: 'center', paddingRight: '30px' }}>
              {title}
            </h3>
            <p className="body-medium" style={{ marginBottom: 'var(--spacing-large)', textAlign: 'center', color: 'var(--text-secondary)' }}>
              {description}
            </p>
            
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 'var(--spacing-medium)' }}>
                <label style={{ display: 'block', marginBottom: 'var(--spacing-xs)', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Vorname *
                </label>
                <div style={{ position: 'relative' }}>
                  <User size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    data-testid="input-firstName"
                    style={{
                      width: '100%',
                      padding: '14px 14px 14px 48px',
                      borderRadius: '20px',
                      border: '2px solid var(--border-medium)',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Dein Vorname"
                  />
                </div>
              </div>

              <div style={{ marginBottom: 'var(--spacing-medium)' }}>
                <label style={{ display: 'block', marginBottom: 'var(--spacing-xs)', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Nachname *
                </label>
                <div style={{ position: 'relative' }}>
                  <User size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    data-testid="input-lastName"
                    style={{
                      width: '100%',
                      padding: '14px 14px 14px 48px',
                      borderRadius: '20px',
                      border: '2px solid var(--border-medium)',
                      fontSize: '1rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Dein Nachname"
                  />
                </div>
              </div>

              <div style={{ marginBottom: 'var(--spacing-medium)' }}>
                <label style={{ display: 'block', marginBottom: 'var(--spacing-xs)', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Telefonnummer (optional)
                </label>
                <div style={{ position: 'relative' }}>
                  <Phone size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    data-testid="input-phone"
                    style={{
                      width: '100%',
                      padding: '14px 14px 14px 48px',
                      borderRadius: '20px',
                      border: '2px solid var(--border-medium)',
                      fontSize: '1rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Deine Telefonnummer"
                  />
                </div>
              </div>

              <div style={{ marginBottom: showLocation ? 'var(--spacing-medium)' : 'var(--spacing-large)' }}>
                <label style={{ display: 'block', marginBottom: 'var(--spacing-xs)', fontWeight: 500, color: 'var(--text-primary)' }}>
                  E-Mail-Adresse *
                </label>
                <div style={{ position: 'relative' }}>
                  <Mail size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    data-testid="input-email"
                    style={{
                      width: '100%',
                      padding: '14px 14px 14px 48px',
                      borderRadius: '20px',
                      border: '2px solid var(--border-medium)',
                      fontSize: '1rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    placeholder="deine@email.de"
                  />
                </div>
              </div>

              {showLocation && (
                <div style={{ marginBottom: 'var(--spacing-large)' }}>
                  <label style={{ display: 'block', marginBottom: 'var(--spacing-xs)', fontWeight: 500, color: 'var(--text-primary)' }}>
                    Gewünschter Standort *
                  </label>
                  <select
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    data-testid="input-location"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      borderRadius: '20px',
                      border: '2px solid var(--border-medium)',
                      fontSize: '1rem',
                      outline: 'none',
                      background: 'white',
                      cursor: 'pointer',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="">Bitte wählen...</option>
                    <option value="Vor Ort - Hamburg Neugraben">Vor Ort - Hamburg Neugraben</option>
                    <option value="Vor Ort - Hamburg Bahrenfeld">Vor Ort - Hamburg Bahrenfeld</option>
                    <option value="Vor Ort - Hamburg Harburg">Vor Ort - Hamburg Harburg</option>
                    <option value="Online">Online</option>
                  </select>
                </div>
              )}

              <div style={{ display: 'flex', gap: 'var(--spacing-medium)' }}>
                <button 
                  type="submit"
                  className="btn-primary"
                  style={{ flex: 1, justifyContent: 'center', display: 'flex', alignItems: 'center', gap: '8px' }}
                  disabled={loading}
                  data-testid="submit-button"
                >
                  {loading ? (
                    <>
                      <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                      Wird gesendet...
                    </>
                  ) : isInquiry ? (
                    'Anfrage senden'
                  ) : (
                    'Weiter zur Zahlung'
                  )}
                </button>
                <button 
                  type="button"
                  onClick={onClose}
                  className="btn-secondary"
                  style={{ flex: 1, justifyContent: 'center' }}
                  data-testid="cancel-button"
                >
                  Abbrechen
                </button>
              </div>
            </form>

            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </>
        )}
      </div>
    </div>
  );
};
